import {
    Button,
    Checkbox,
    DatePickerWithIcon,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
} from '@/shared/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { z } from 'zod'
import { toUTCDate } from '@/shared/lib/date'

export type CustomActFormValues = {
    number: string
    date: Date
    dateStart: Date
    dateEnd: Date
    budget: number
    is_budget_set_automatically: boolean
    is_date_start_set_automatically: boolean
    is_date_end_set_automatically: boolean
}

type CustomActFormProps = {
    open: boolean
    setOpen: (open: boolean) => void
    onSubmit: (data: CustomActFormValues) => Promise<void> | void
    mode?: 'create' | 'edit'
    initialValues?: CustomActFormValues
    isSubmitting?: boolean
    autoDateStart: Date
    autoDateEnd: Date
    autoBudget: number
    contractStartDate: Date
    isLinkDeleted: boolean
}

const createSchema = (autoBudget: number) =>
    z
        .object({
            number: z.string().min(1, 'Номер акта обязателен'),
            date: z.date({
                message: 'Дата выставления обязательна',
            }),
            dateStart: z.date({
                message: 'Дата начала обязательна',
            }),
            dateEnd: z.date({
                message: 'Дата окончания обязательна',
            }),
            budget: z
                .number({
                    error: 'Бюджет обязателен',
                })
                .min(
                    autoBudget,
                    'Бюджет не может быть меньше автоматически установленного'
                ),
            is_budget_set_automatically: z.boolean(),
            is_date_start_set_automatically: z.boolean(),
            is_date_end_set_automatically: z.boolean(),
        })
        .refine(
            (data) => {
                return data.dateEnd >= data.dateStart
            },
            {
                error: 'Дата окончания не может быть раньше даты начала',
                path: ['dateEnd'],
            }
        )
        .refine(
            (data) => {
                return data.dateEnd <= data.date
            },
            {
                error: 'Дата окончания не может быть больше даты выставления',
                path: ['dateEnd'],
            }
        )

const CustomActForm = ({
    open,
    setOpen,
    onSubmit,
    mode = 'create',
    initialValues,
    isSubmitting = false,
    autoDateStart,
    autoDateEnd,
    autoBudget,
    contractStartDate,
    isLinkDeleted,
}: CustomActFormProps) => {
    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isSubmitting: isFormSubmitting },
    } = useForm<CustomActFormValues>({
        resolver: zodResolver(createSchema(autoBudget)),
        defaultValues: {
            number: initialValues?.number ?? '',
            date: initialValues?.date ?? new Date(),
            dateStart: initialValues?.dateStart ?? autoDateStart,
            dateEnd: initialValues?.dateEnd ?? autoDateEnd,
            budget: initialValues?.budget ?? 0,
            is_budget_set_automatically:
                initialValues?.is_budget_set_automatically ?? true,
            is_date_start_set_automatically:
                initialValues?.is_date_start_set_automatically ?? true,
            is_date_end_set_automatically:
                initialValues?.is_date_end_set_automatically ?? true,
        },
    })

    const dateStartValue = watch('dateStart')
    const isDateStartAuto = watch('is_date_start_set_automatically')
    const isDateEndAuto = watch('is_date_end_set_automatically')
    const isBudgetAuto = watch('is_budget_set_automatically')

    useEffect(() => {
        if (!open) {
            return
        }

        reset({
            number: initialValues?.number ?? '',
            date: initialValues?.date ?? new Date(),
            dateStart: initialValues?.dateStart ?? autoDateStart,
            dateEnd: initialValues?.dateEnd ?? autoDateEnd,
            budget: initialValues?.budget ?? 0,
            is_budget_set_automatically:
                initialValues?.is_budget_set_automatically ?? true,
            is_date_start_set_automatically:
                initialValues?.is_date_start_set_automatically ?? true,
            is_date_end_set_automatically:
                initialValues?.is_date_end_set_automatically ?? true,
        })
    }, [autoDateEnd, autoDateStart, initialValues, open, reset])

    const onSubmitForm = handleSubmit(async (data) => {
        await onSubmit(data)
    })

    const submitInProgress = isSubmitting || isFormSubmitting
    const isEditMode = mode === 'edit'

    const title = isEditMode ? 'Редактировать акт' : 'Добавить акт'

    return (
        <Dialog open={open} onOpenChange={setOpen} modal={false}>
            <DialogContent
                aria-describedby={undefined}
                onInteractOutside={(event) => event.preventDefault()}
                onPointerDownOutside={(event) => event.preventDefault()}
                className="left-auto right-16 top-[10vh] max-h-[90vh] w-[505px] translate-x-0 translate-y-0 gap-0 overflow-y-auto bg-white px-10 py-[30px] shadow-[0px_4px_24px_0px_rgba(0,0,0,0.15)]"
            >
                <DialogHeader className="w-full justify-center pb-4 text-xs font-black">
                    <DialogTitle className="flex w-full items-center">
                        <div className="flex items-center gap-3">
                            <span className="text-[22px]">{title}</span>
                        </div>
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={onSubmitForm} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label className="text-sm">Номер акта</Label>
                        <Controller
                            control={control}
                            name="number"
                            render={({ field }) => (
                                <Input
                                    placeholder="Введите номер"
                                    {...field}
                                    error={errors.number?.message}
                                />
                            )}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className="text-sm">Дата выставления</Label>
                        <Controller
                            control={control}
                            name="date"
                            render={({ field }) => (
                                <DatePickerWithIcon
                                    selected={field.value}
                                    onChange={(date) => {
                                        const newDate = toUTCDate(
                                            date || new Date()
                                        )

                                        field.onChange(newDate)
                                        if (isDateEndAuto && !isLinkDeleted) {
                                            setValue('dateEnd', newDate)
                                        }
                                    }}
                                    dateFormat="dd.MM.yy"
                                    dropdownMode="scroll"
                                    className="text-xs"
                                    minDate={contractStartDate}
                                />
                            )}
                        />
                        {errors.date?.message && (
                            <span className="text-[10px] italic text-[#FF5957]">
                                {errors.date.message}
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className="text-sm">Дата начала</Label>
                        <Controller
                            control={control}
                            name="dateStart"
                            render={({ field }) => (
                                <DatePickerWithIcon
                                    selected={field.value}
                                    onChange={(date) =>
                                        field.onChange(
                                            toUTCDate(date ?? field.value)
                                        )
                                    }
                                    dateFormat="dd.MM.yy"
                                    dropdownMode="scroll"
                                    className="text-xs"
                                    minDate={contractStartDate}
                                    maxDate={watch('date')}
                                    isDisabled={isDateStartAuto}
                                />
                            )}
                        />
                        {errors.dateStart?.message && (
                            <span className="text-[10px] italic text-[#FF5957]">
                                {errors.dateStart.message}
                            </span>
                        )}

                        <div className="flex items-center gap-1">
                            <Controller
                                control={control}
                                name="is_date_start_set_automatically"
                                render={({ field }) => (
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => {
                                            const checked = e.target.checked
                                            field.onChange(checked)

                                            if (checked) {
                                                setValue(
                                                    'dateStart',
                                                    toUTCDate(autoDateStart)
                                                )
                                            }
                                        }}
                                        id="is_date_start_set_automatically"
                                    />
                                )}
                            />
                            <Label
                                className="block text-xs"
                                htmlFor="is_date_start_set_automatically"
                            >
                                Совпадает с датой публикации креатива
                            </Label>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className="text-sm">Дата окончания</Label>
                        <Controller
                            control={control}
                            name="dateEnd"
                            render={({ field }) => (
                                <DatePickerWithIcon
                                    selected={field.value}
                                    onChange={(date) =>
                                        field.onChange(
                                            toUTCDate(date ?? field.value)
                                        )
                                    }
                                    dateFormat="dd.MM.yy"
                                    dropdownMode="scroll"
                                    className="text-xs"
                                    minDate={dateStartValue}
                                    maxDate={
                                        watch('date') > new Date()
                                            ? watch('date')
                                            : new Date()
                                    }
                                    isDisabled={isDateEndAuto}
                                />
                            )}
                        />
                        {errors.dateEnd?.message && (
                            <span className="text-[10px] italic text-[#FF5957]">
                                {errors.dateEnd.message}
                            </span>
                        )}

                        <div className="flex items-center gap-1">
                            <Controller
                                control={control}
                                name="is_date_end_set_automatically"
                                render={({ field }) => (
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => {
                                            const checked = e.target.checked
                                            field.onChange(checked)

                                            if (checked) {
                                                setValue('dateEnd', autoDateEnd)
                                            }
                                        }}
                                        id="is_date_end_set_automatically"
                                    />
                                )}
                            />
                            <Label
                                className="block text-xs"
                                htmlFor="is_date_end_set_automatically"
                            >
                                Дата окончания устанавливается автоматически
                            </Label>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className="text-sm">Бюджет</Label>
                        <Controller
                            control={control}
                            name="budget"
                            render={({ field }) => (
                                <Input
                                    type="number"
                                    min={0}
                                    value={field.value}
                                    onChange={(value) =>
                                        field.onChange(parseFloat(value))
                                    }
                                    error={errors.budget?.message}
                                    placeholder="Сумма, ₽"
                                    shouldShowClearButton={false}
                                    disabled={isBudgetAuto}
                                />
                            )}
                        />

                        <div className="flex items-center gap-1">
                            <Controller
                                control={control}
                                name="is_budget_set_automatically"
                                render={({ field }) => (
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => {
                                            const checked = e.target.checked
                                            field.onChange(checked)

                                            if (checked) {
                                                setValue(
                                                    'budget',
                                                    autoBudget ?? 0
                                                )
                                            }
                                        }}
                                        id="is_budget_set_automatically"
                                    />
                                )}
                            />
                            <Label
                                className="block text-xs"
                                htmlFor="is_budget_set_automatically"
                            >
                                Бюджет устанавливается автоматически
                            </Label>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setOpen(false)}
                            className="flex-1"
                            disabled={submitInProgress}
                        >
                            Отмена
                        </Button>
                        <Button
                            type="submit"
                            className="flex-1"
                            disabled={submitInProgress}
                        >
                            {isEditMode ? 'Сохранить' : 'Добавить'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default CustomActForm
